// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { Sparkles } from "lucide-react";
import {
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import type { MilestoneAnalysisChartData } from "./activity-charts";
import type { VisibleTimeRange } from "./formatters";

interface MilestoneAnalysesparkleProps {
  cx: number;
  cy: number;
  payload?: {
    id: string;
    timestamp: number;
  };
  onDateClick?: (date: string) => void;
}

// Custom component for rendering milestone analysis sparkles
function MilestoneAnalysesparkle({
  cx,
  cy,
  payload,
  onDateClick,
}: MilestoneAnalysesparkleProps) {
  if (!payload) return null;

  const visualSize = 14;
  const clickableArea = 20; // Larger clickable area

  return (
    <g
      style={{ cursor: "pointer" }}
      onClick={() => {
        // Also trigger date click for navigation
        if (payload.timestamp && onDateClick) {
          onDateClick(new Date(payload.timestamp).toISOString());
        }
      }}
    >
      {/* Larger transparent circle for better click targeting */}
      <circle cx={cx} cy={cy} r={clickableArea / 2} fill="transparent" />
      {/* Actual visual sparkle icon */}
      <g
        transform={`translate(${cx - visualSize / 2}, ${cy - visualSize / 2})`}
      >
        <Sparkles size={visualSize} className="text-warning" strokeWidth={2} />
      </g>
    </g>
  );
}

interface MilestoneAnalysesChartProps {
  MilestoneAnalyses?: MilestoneAnalysisChartData[];
  domain: [number, number];
  ticks: number[];
  onDateClick?: (date: string) => void;
  isLoading: boolean;
  visibleRange?: { start: string; end: string };
  visibleTimeRange: VisibleTimeRange | null;
}

export function MilestoneAnalysesChart({
  MilestoneAnalyses,
  domain,
  ticks,
  onDateClick,
  isLoading,
  visibleRange,
}: MilestoneAnalysesChartProps) {
  // Format milestone analysis for chart display - this is simplified since we already have the data formatted
  const formattedMilestoneAnalyses = MilestoneAnalyses || [];

  if (isLoading) {
    return <Skeleton className="h-full w-full" />;
  }

  // Handle click on chart
  const handleChartClick = (data: { activeLabel?: string | number }): void => {
    if (data?.activeLabel && onDateClick) {
      try {
        // Convert to number if it's a string
        const timestamp =
          typeof data.activeLabel === "string"
            ? parseInt(data.activeLabel, 10)
            : data.activeLabel;

        // Validate timestamp before converting to ISO string
        if (Number.isFinite(timestamp) && timestamp > 0) {
          onDateClick(new Date(timestamp).toISOString());
        }
      } catch (error) {
        console.error(
          "Invalid date value in chart click:",
          data.activeLabel,
          error,
        );
      }
    }
  };

  return (
    <div className="relative w-full h-full overflow-visible">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          margin={{ top: 0, right: 20, left: 25, bottom: 0 }}
          onClick={handleChartClick}
        >
          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={domain}
            ticks={ticks}
            interval={0}
            axisLine={false}
            tickLine={false}
            tick={false}
            height={0}
          />
          <YAxis yAxisId="milestone-analyses" hide domain={[0, 1]} />

          {/* Milestone Analysis Scatter Points */}
          {formattedMilestoneAnalyses.length > 0 && (
            <Scatter
              data={formattedMilestoneAnalyses.map((moment) => ({
                ...moment,
                y: 0.5, // Position in the middle of the chart instead of at the top
              }))}
              dataKey="y"
              yAxisId="milestone-analyses"
              shape={(props: unknown) => {
                // Quick null/undefined check
                if (!props || typeof props !== "object") {
                  return <g />;
                }

                // Use direct type assertion for the expected shape
                const scatterProps = props as MilestoneAnalysesparkleProps;

                // Return empty group if essential props are missing
                if (
                  typeof scatterProps.cx !== "number" ||
                  typeof scatterProps.cy !== "number"
                ) {
                  return <g />;
                }

                return (
                  <MilestoneAnalysesparkle
                    cx={scatterProps.cx}
                    cy={scatterProps.cy}
                    payload={scatterProps.payload}
                    onDateClick={onDateClick}
                  />
                );
              }}
              isAnimationActive={false}
            />
          )}
          {/* Add visible range indicators */}
          {visibleRange && (
            <>
              <ReferenceLine
                yAxisId="milestone-analyses"
                x={Math.max(domain[0], new Date(visibleRange.start).getTime())}
                stroke="hsl(var(--chart-5))"
                strokeWidth={0.5}
                className="transition-all duration-200 ease-out"
              />
              <ReferenceLine
                yAxisId="milestone-analyses"
                x={Math.min(domain[1], new Date(visibleRange.end).getTime())}
                stroke="hsl(var(--chart-5))"
                strokeWidth={0.5}
                className="transition-all duration-200 ease-out"
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
